import axios from 'axios';
import Swal from 'sweetalert2';
import { BASE_URL, UPLOAD_ROOT_PATH, zipURL as downloadURL } from './Constants';
import { formatBytes } from './commonFunction';

/**
 * Downloads a file with progress tracking, optional Swal UI, and optional URL rewriting.
 *
 * @param {string} fileUrl - The URL of the file to download.
 * @param {string} fileName - The name to save the downloaded file as.
 * @param {boolean} [useCorona=true] - Whether to prepend the downloadURL proxy to the file URL.
 * @param {boolean} [showSwal=true] - Whether to display SweetAlert progress UI during the download.
 *
 * @returns {Promise<void>} Resolves when the download either completes or fails.
 */
export const download = async (fileUrl, fileName, useCorona = true, showSwal = true) => {
    try {
        // Validate inputs
        if (!fileUrl || !fileName) {
            throw new Error('Invalid file URL or filename');
        }

        let finalURL = fileUrl;

        // ✅ Only apply corona logic when required
        if (useCorona) {
            finalURL = buildAbsoluteDownloadUrl(fileUrl);

            // Remove port numbers (kept from your original logic)
            finalURL = finalURL.replace(/:\d{4,5}/g, '');
        }

        // Show Swal only if enabled
        if (showSwal) {
            Swal.fire({
                title: 'File Downloading...',
                icon: 'info',
                html: `
          <div class="downloading-progress">
            <div class="progress w-100 mb-2 mt-3">
              <div class="progress-bar progress-bar-striped progress-bar-animated"
                  id="progress-bar"
                  role="progressbar"
                  aria-valuenow="0"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  style="width: 0%;">
                <span id="progress-text">0%</span>
              </div>
            </div>
            <div id="download-info"></div>
          </div>
        `,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                },
            });
        }

        let realDownloadStarted = false;

        const response = await axios({
            url: finalURL,
            method: 'GET',
            responseType: 'blob',
            validateStatus: (status) => status >= 200 && status < 300,
            onDownloadProgress: (e) => {
                // If Swal disabled, no UI updates
                if (!showSwal) return;

                if (!e.total || e.total === 0 || e.loaded === 0) return;

                if (!realDownloadStarted) {
                    realDownloadStarted = true;
                    Swal.hideLoading();
                }

                const percent = Math.round((e.loaded * 100) / e.total);

                const progressBar = document.getElementById('progress-bar');
                const progressText = document.getElementById('progress-text');
                const downloadInfo = document.getElementById('download-info');

                if (progressBar) {
                    progressBar.style.width = `${percent}%`;
                    progressBar.setAttribute('aria-valuenow', percent);
                }
                if (progressText) progressText.textContent = `${percent}%`;
                if (downloadInfo) downloadInfo.textContent = `${formatBytes(e.loaded)} / ${formatBytes(e.total)}`;
            },
        });

        // Trigger browser download
        const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = blobUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();

        setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        }, 100);

        if (showSwal) {
            Swal.close();
            Swal.fire({
                icon: 'success',
                title: 'Download Complete',
                timer: 2000,
                showConfirmButton: true,
            });
        }
    } catch (error) {
        Swal.close();
        Swal.fire({
            icon: 'error',
            title: 'Error while downloading file',
            showConfirmButton: true,
        });
    }
};

/**
 * Removes public/ and `${UPLOAD_ROOT_PATH}/` path to prepend a base url.
 * @param {string} filePath Path to transform
 * @returns clean relative file path
 */
export const createCleanFilePath = (filePath) => {
    if (!filePath) {
        return filePath;
    }

    return filePath.replaceAll(`${UPLOAD_ROOT_PATH}/`, '').replace('public/', '');
};

/**
 * Prepends downloadURL (e.g. 'https://corona.myaie.ac/') safely
 * by removing UPLOAD_ROOT_PATH (e.g. '/home/myaie/public_html').
 *
 * @param {string} filePath * filePath Examples:
 * filePath = "/home/myaie/public_html/Library/feedResources/file.pdf"
 * filePath = "/home/myaie/api/public/Library/feedResources/file.pdf"
 * filePath = "/home/myaie/staging/api/public/Library/file.pdf"
 * filePath = "https://corona.myaie.ac/myaie.ac/Library/file.pdf"
 * filePath = "/somePath/Library.some.pdf"
 * filePath = "Library/feedResources/file.pdf"
 * filePath = "https://example.com/file.pdf" (unchanged)
 * filePath = "http://localhost:3000/file.pdf" (unchanged)
 * @returns clean absolute downloadable URL
 */
export const buildAbsoluteDownloadUrl = (filePath) => {
    if (!filePath) return filePath;

    // Already correct corona URL
    if (filePath.includes(downloadURL)) {
        return filePath;
    }

    const cleanDownloadUrl = downloadURL.replace(/\/$/, '');
    const baseHost = BASE_URL.replace(/^https?:\/\//, '').replace(/\/$/, '');

    let cleaned = filePath.replace(/^https?:\/\//, '').replace(/\\/g, '/');

    let relativePath = cleaned.replace(/.*\/api\/public\//, '');

    if (relativePath === cleaned) {
        relativePath = cleaned.replace(/.*\/public_html\//, '');
    }

    relativePath = createCleanFilePath(relativePath);

    const isKnownPath =
        relativePath !== cleaned ||
        cleaned.includes('/public_html/') ||
        cleaned.includes('/api/public/') ||
        cleaned.startsWith(baseHost) ||
        cleaned.startsWith(`www.${baseHost}`);

    // Unknown structure
    if (!isKnownPath) {
        if (isValidUrl(filePath)) {
            return encodeUrlSafely(filePath);
        }

        const relative = cleaned.replace(/^\/+/, '');
        return `${cleanDownloadUrl}/${baseHost}/${encodeURI(relative)}`;
    }

    let finalRelative = relativePath.replace(/^\/+/, '');
    if (finalRelative.startsWith(`www.${baseHost}`)) {
        finalRelative = finalRelative.slice(`www.${baseHost}`.length).replace(/^\/+/, '');
    } else if (finalRelative.startsWith(baseHost)) {
        finalRelative = finalRelative.slice(baseHost.length).replace(/^\/+/, '');
    }

    const encodedFinal = finalRelative
        .split('/')
        .map((segment) => encodeURIComponent(decodeURIComponent(segment)))
        .join('/');

    return `${cleanDownloadUrl}/${baseHost}/${encodedFinal}`;
};

/**
 * Tests if this is a valid URL.
 * @param {string} url
 * @returns
 */
const isValidUrl = (url) => {
    return /^https?:\/\//i.test(url);
};

/**
 * Encode URL safely.
 * @param {string} url
 * @returns valid URL.
 */
const encodeUrlSafely = (url) => {
    try {
        const parsed = new URL(url);

        // Encode only the pathname (keeps domain/query intact)
        parsed.pathname = parsed.pathname
            .split('/')
            .map((segment) => encodeURIComponent(decodeURIComponent(segment)))
            .join('/');

        return parsed.toString();
    } catch {
        return url;
    }
};
